import React, { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { Button } from "../ui/Button";
import {
  getAssetLocations,
  createAssetLocation,
  deleteAssetLocation,
} from "../../services/assetLocations";

// Modal "Cadastro de Localização" — listar / adicionar / excluir localizações
// dos ativos. Aberto de dentro do Novo/Editar Ativo.
export default function LocationCatalogModal({ isOpen, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => getAssetLocations().then(setItems).catch(() => setItems([]));

  useEffect(() => {
    if (isOpen) {
      load();
      setName("");
      setErr("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setErr("");
    try {
      await createAssetLocation(n);
      setName("");
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível cadastrar a localização.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Excluir a localização "${loc.name}"?`)) return;
    setBusy(true);
    setErr("");
    try {
      await deleteAssetLocation(loc.id);
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível excluir.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Cadastro de Localização</h2>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-500">
          Cadastre e gerencie as localizações usadas nos ativos.
        </p>

        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Ex.: TI / Hangar 4 / Catarina / Brasil"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button type="button" onClick={add} disabled={busy || !name.trim()}>
            Adicionar
          </Button>
        </div>
        {err && <p className="mt-2 text-sm text-red-600">{err}</p>}

        <div className="mt-4 max-h-72 divide-y divide-gray-100 overflow-auto">
          {items.length === 0 ? (
            <p className="py-3 text-sm text-gray-500">Nenhuma localização cadastrada ainda.</p>
          ) : (
            items.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-700">{l.name}</span>
                <button
                  type="button"
                  onClick={() => remove(l)}
                  disabled={busy}
                  title="Excluir"
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  );
}
